import './Opportunity.css';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BrandsTable from '../../components/BrandsTable/BrandsTable';
import KeyInsights from '../../components/KeyInsights/KeyInsights';
import BrandFilters from '../../components/BrandFilters/BrandFilters';
import AddToListDialog from '../../components/AddToListDialog/AddToListDialog';
import { BrandData, fetchKeyInsights, KeyInsightsData, fetchUltimateParentData } from '../../services/brandService';
import { fetchUserBrandMeta } from '../../services/listService';
import { useOpportunityStore } from '../../stores';
import { useConfigurationStore } from '../../stores/configurationStore';

// Data cache to prevent refetching
const dataCache = new Map<string, { data: BrandData[]; totalCount: number; timestamp: number }>();
const CACHE_DURATION = 15 * 60 * 1000; // 15 minutes

function Opportunity() {
  const navigate = useNavigate();
  const opportunityStore = useOpportunityStore();
  const configStore = useConfigurationStore();

  // Use store values instead of local state
  const [brandData, setBrandData] = useState<BrandData[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [keyInsightsLoading, setKeyInsightsLoading] = useState(true);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [listDialogOpen, setListDialogOpen] = useState(false);
  const [selectedBrandForList, setSelectedBrandForList] = useState('');
  const [savedBrands, setSavedBrands] = useState<Set<string>>(new Set());
  const [userNotesCount, setUserNotesCount] = useState<Map<string, number>>(new Map());
  const [keyInsights, setKeyInsights] = useState<KeyInsightsData>({
    totalServiceableSites: 0,
    totalAcquiredSites: 0,
    retailersWithNoPenetration: 0,
    highCrimeSites: 0,
    highGkapSites: 0,
    totalOpportunities: 0,
    newInstallCount: 0,
    existingInstallCount: 0
  });

  // Use store values for pagination state and filters
  const offset = opportunityStore.offset;
  const pageSize = opportunityStore.pageSize;
  const sortBy = opportunityStore.sortBy;
  const searchInput = opportunityStore.searchInput;
  const searchQuery = opportunityStore.searchQuery;
  const filters = opportunityStore.filters;

  // Get configuration from store
  const { purchek, containment, cartManager } = configStore;

  // Handle debounced search input
  const handleSearchChange = (value: string) => {
    // Only update the input display, don't update table data
    opportunityStore.setSearchInput(value);
  };

  // Fetch key insights on component mount
  useEffect(() => {
    const loadKeyInsights = async () => {
      setKeyInsightsLoading(true);
      const insights = await fetchKeyInsights();
      setKeyInsights(insights);
      setKeyInsightsLoading(false);
    };

    loadKeyInsights();
    loadUserBrandMeta();
  }, []);

  const loadUserBrandMeta = async () => {
    const meta = await fetchUserBrandMeta();
    setSavedBrands(meta.savedBrandsSet);
    setUserNotesCount(meta.notesCountMap);
  };

  // Fetch ultimate parent data on component mount or when pagination/filter/search/sort changes
  useEffect(() => {
    const loadUltimateParentData = async () => {
      try {
        setLoading(true);

        const cacheKey = `${pageSize}-${offset}-${sortBy}-${filters.retailerSegment}-${filters.opportunitiesMin}-${filters.opportunitiesMax}-${filters.retailerSitesMin}-${filters.retailerSitesMax}`;

        // Check if data is in cache and not expired
        const cachedData = dataCache.get(cacheKey);
        if (cachedData && Date.now() - cachedData.timestamp < CACHE_DURATION) {
          console.log('Loading from cache:', cacheKey);
          setBrandData(cachedData.data);
          setTotalCount(cachedData.totalCount);
          setLoading(false);
          return;
        }

        // Fetch fresh data - using ultimate parent data instead of brands
        const result = await fetchUltimateParentData(
          pageSize,
          offset,
          sortBy,
          filters,
          purchek,
          containment,
          cartManager
        );

        const data = result.data || [];
        const total = result.totalCount || 0;

        // Cache the data
        dataCache.set(cacheKey, {
          data,
          totalCount: total,
          timestamp: Date.now()
        });

        setBrandData(data);
        setTotalCount(total);
      } catch (error) {
        console.error('Failed to load ultimate parent data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUltimateParentData();
  }, [offset, pageSize, sortBy, filters, purchek, containment, cartManager]);

  // Refetch data when configuration changes
  useEffect(() => {
    const handleConfigurationChange = () => {
      dataCache.clear();
      const loadUltimateParentData = async () => {
        try {
          setLoading(true);
          const result = await fetchUltimateParentData(
            pageSize,
            offset,
            sortBy,
            filters,
            purchek,
            containment,
            cartManager
          );
          setBrandData(result.data);
          setTotalCount(result.totalCount);
        } catch (error) {
          console.error('Failed to load ultimate parent data:', error);
        } finally {
          setLoading(false);
        }
      };
      loadUltimateParentData();
    };

    window.addEventListener('configurationSaved', handleConfigurationChange);
    return () => window.removeEventListener('configurationSaved', handleConfigurationChange);
  }, [pageSize, offset, searchQuery, sortBy, filters, purchek, containment, cartManager]);


  // Handle sort change - reset to first page and clear cache for fresh data
  const handleSortChange = (newSort: string) => {
    dataCache.clear();
    opportunityStore.setSortBy(newSort);
  };

  // Handle filter apply
  const handleApplyFilters = (appliedFilters: any) => {
    opportunityStore.setFilters(appliedFilters);
    dataCache.clear();
    setIsFilterPanelOpen(false);
  };


  // Handle ultimate parent click navigation
  const handleBrandClick = (brand: BrandData) => {
    // Navigate to opportunity details with ultimateparent type
    navigate(`/dashboard/opportunitydetails/${encodeURIComponent(brand.name)}?type=ultimateparent`);
  };

  // Calculate active filter count
  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.retailerSegment !== null) count++;
    if (filters.opportunitiesMin > 0 || filters.opportunitiesMax < 999999999) count++;
    if (filters.retailerSitesMin > 0 || filters.retailerSitesMax < 999999999) count++;
    return count;
  };

  // Handle add to list click
  const handleAddToListClick = (brandName: string) => {
    setSelectedBrandForList(brandName);
    setListDialogOpen(true);
  };

  // Handle notes refetch - refresh user-specific notes count + saved status
  const handleNotesRefetch = async () => {
    await loadUserBrandMeta();
  };

  return (
    <div className="opportunity-page">
      <KeyInsights
        totalServiceableSites={keyInsights.totalServiceableSites}
        totalAcquiredSites={keyInsights.totalAcquiredSites}
        retailersWithNoPenetration={keyInsights.retailersWithNoPenetration}
        totalOpportunities={keyInsights.totalOpportunities}
        highCrimeSites={keyInsights.highCrimeSites}
        highCrimeSiteRetailers={0}
        highGkapSites={keyInsights.highGkapSites}
        highGkapRetailers={0}
        newInstallCount={keyInsights.newInstallCount}
        existingCount={keyInsights.existingInstallCount}
        isLoading={keyInsightsLoading}
      />

      <BrandsTable
        data={brandData}
        loading={loading}
        offset={offset}
        pageSize={pageSize}
        totalCount={totalCount}
        onOffsetChange={opportunityStore.setOffset}
        onPageSizeChange={opportunityStore.setPageSize}
        onBrandClick={handleBrandClick}
        searchTerm={searchInput}
        onSearchChange={handleSearchChange}
        sortBy={sortBy}
        onSortChange={handleSortChange}
        onFilterClick={() => setIsFilterPanelOpen(true)}
        onNotesRefetch={handleNotesRefetch}
        activeFilterCount={getActiveFilterCount()}
        onAddToListClick={handleAddToListClick}
        savedBrands={savedBrands}
        userNotesCount={userNotesCount}
      />

      <BrandFilters
        isOpen={isFilterPanelOpen}
        onClose={() => setIsFilterPanelOpen(false)}
        onApplyFilters={handleApplyFilters}
      />

      <AddToListDialog
        isOpen={listDialogOpen}
        onClose={() => { setListDialogOpen(false); loadUserBrandMeta(); }}
        type="brand"
        brandName={selectedBrandForList}
      />
    </div>
  );
}

export default Opportunity;
